import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { fn } from '../api/functions';
import { Link } from 'react-router-dom';
import { BookOpenIcon, SparklesIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserAccess } from '../hooks/useUserAccess';
import SecureStoryShareModal from '../components/SecureStoryShareModal';
import PDFGenerator from '../components/PDFGenerator';

export const DashboardPage = () => {
  const { currentUser } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const { userAccess, loading: accessLoading, canCreateStory, remainingTokens, hasAccess } = useUserAccess();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const location = useLocation();
  const [stories, setStories] = useState([]);
  const [myGiftStories, setMyGiftStories] = useState([]); // Stories I created as gifts
  const [receivedGiftStories, setReceivedGiftStories] = useState([]); // Stories gifted to me
  const [collaborativeStories, setCollaborativeStories] = useState([]);
  const [sharedStories, setSharedStories] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [developerMode, setDeveloperMode] = useState(false);
  // Reminder UI state
  const [reminderStoryId, setReminderStoryId] = useState(null); // story currently editing reminder
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({ cadence: 'off' });
  const reminderDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!reminderStoryId) return;
    const handleClick = (e) => {
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(e.target)) {
        setReminderStoryId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [reminderStoryId]);

  const openReminderPanel = async (storyId) => {
    if (reminderStoryId === storyId) {
      // toggle off
      setReminderStoryId(null);
      return;
    }
    setReminderStoryId(storyId);
    setReminderLoading(true);
    try {
      const res = await fn.getStoryReminder({ storyId });
      if (res.data.success) {
        setReminderSettings(res.data.reminder || { cadence: 'off' });
      }
    } catch (e) {
      console.warn('Failed to load reminder settings', e);
      setReminderSettings({ cadence: 'off' });
    } finally {
      setReminderLoading(false);
    }
  };

  const saveReminderCadence = async (cadence) => {
    if (!reminderStoryId) return;
    setReminderSaving(true);
    try {
      const res = await fn.setStoryReminder({ storyId: reminderStoryId, cadence });
      if (res.data.success) {
        setReminderSettings(res.data.reminder);
        setSuccessMessage('Reminder settings saved');
        setTimeout(()=>setSuccessMessage(null),4000);
      }
    } catch (e) {
      alert('Failed to save reminder');
    } finally {
      setReminderSaving(false);
    }
  };
  
  // State for secure story sharing functionality  
  const [secureShareModal, setSecureShareModal] = useState({ isOpen: false, story: null });
  
  // State for sharing links functionality  
  const [sharingModal, setSharingModal] = useState({ isOpen: false, storyId: null, links: null });
  // eslint-disable-next-line no-unused-vars
  const [loadingSharingLink, setLoadingSharingLink] = useState(false);

  // State for PDF generation
  const [pdfGenerationModal, setPdfGenerationModal] = useState({ isOpen: false, storyId: null, storyTitle: null });

  // Show success message if passed from navigation
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location.state]);

  useEffect(() => {
    if (!currentUser) return;

    let unsubscribe = null;

    // Fetch stories
    const setupStoriesListener = () => {
      try {
        const allStories = new Map(); // Use Map to avoid duplicates by ID

        const updateStoriesState = () => {
          // Convert Map to array and sort by updatedAt
          const storyList = Array.from(allStories.values()).sort((a, b) => {
            const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
            const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
            return bTime - aTime;
          });

          // Separate personal stories from gift stories
          const personalStories = [];
          const createdGifts = [];
          const receivedGifts = [];

          storyList.forEach(story => {
            if (story.isGiftStory) {
              // Check if I am the recipient (by email match)
              const isRecipient = story.giftAccess?.recipientEmail === currentUser.email;
              
              if (isRecipient) {
                receivedGifts.push(story);
              } else {
                // If not recipient, assume I created it (since I have access to it via these queries)
                createdGifts.push(story);
              }
            } else {
              personalStories.push(story);
            }
          });
          
          setStories(personalStories);
          setMyGiftStories(createdGifts);
          setReceivedGiftStories(receivedGifts);
          setLoading(false);
        };

        // Query 1: Stories I own directly
        const myStoriesQuery = query(
          collection(db, 'stories'),
          where('userId', '==', currentUser.uid),
          orderBy('updatedAt', 'desc')
        );

        const unsubscribe1 = onSnapshot(myStoriesQuery, (snapshot) => {
          snapshot.docs.forEach(doc => {
            allStories.set(doc.id, { id: doc.id, ...doc.data() });
          });
          updateStoriesState();
        }, (error) => {
          console.error('Error in owned stories query:', error);
        });

        // Query 2: Gift stories where I'm the recipient
        const giftRecipientQuery = query(
          collection(db, 'stories'),
          where('giftAccess.recipientEmail', '==', currentUser.email)
        );

        const unsubscribe2 = onSnapshot(giftRecipientQuery, (snapshot) => {
          snapshot.docs.forEach(doc => {
            allStories.set(doc.id, { id: doc.id, ...doc.data() });
          });
          updateStoriesState();
        }, (error) => {
          console.error('Error in gift recipient query:', error);
        });

        // Query 3: Gift stories where I'm the gifter
        const giftGiverQuery = query(
          collection(db, 'stories'),
          where('giftAccess.gifter.email', '==', currentUser.email)
        );

        const unsubscribe3 = onSnapshot(giftGiverQuery, (snapshot) => {
          snapshot.docs.forEach(doc => {
            allStories.set(doc.id, { id: doc.id, ...doc.data() });
          });
          updateStoriesState();
        }, (error) => {
          console.error('Error in gift giver query:', error);
        });

        // Store combined unsubscriber
        unsubscribe = () => {
          unsubscribe1();
          unsubscribe2();
          unsubscribe3();
        };

      } catch (error) {
        console.error('Error setting up stories listener:', error);
        setStories([]);
        setMyGiftStories([]);
        setLoading(false);
      }
    };

    // Fetch user stats
    const fetchUserStats = async () => {
      try {
        const result = await fn.getUserStats();
        if (result.data.success) {
          setUserStats(result.data.stats);
        }
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };

    // Fetch collaborative stories
    const fetchCollaborativeStories = async () => {
      try {
        const result = await fn.getCollaborativeStories();
        if (result.data.success) {
          setCollaborativeStories(result.data.stories);
        }
      } catch (error) {
        console.error('Error fetching collaborative stories:', error);
        setCollaborativeStories([]);
      }
    };

    // Fetch shared stories (stories shared with me via email invitation)
    const fetchSharedStories = async () => {
      try {
        const result = await fn.getSharedStories();
        if (result.data.success) {
          setSharedStories(result.data.stories);
        }
      } catch (error) {
        console.error('Error fetching shared stories:', error);
        setSharedStories([]);
      }
    };

    setupStoriesListener();
    fetchUserStats();
    fetchCollaborativeStories();
    fetchSharedStories();

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [currentUser]);

  const handleDeleteStory = async (storyId, storyTitle) => {
    const confirmMessage = `Are you sure you want to delete "${storyTitle}"?\n\nThis will permanently delete:\n• The story document\n• All recorded answers and audio files\n• All uploaded photos\n• Everything cannot be recovered\n\nType "DELETE" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'DELETE') {
      if (userInput !== null) {
        alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      }
      return;
    }

    try {
      console.log(`🗑️ Starting deletion of story: ${storyId}`);
      
      const result = await fn.deleteStoryCompletely({ storyId });
      
      console.log('✅ Story deletion completed:', result.data);
      
      // Remove from local state immediately
      setStories(prev => prev.filter(story => story.id !== storyId));
      setMyGiftStories(prev => prev.filter(story => story.id !== storyId));
      
      // Show success message
      setSuccessMessage(result.data.message);
      setTimeout(() => setSuccessMessage(null), 8000);
      
    } catch (error) {
      console.error('❌ Error deleting story:', error);
      alert(`Failed to delete story: ${error.message}`);
    }
  };

  const handleExportPDF = async (storyId, storyTitle) => {
    // Open the PDF generation modal
    setPdfGenerationModal({ isOpen: true, storyId, storyTitle });
  };

  const handleResendGiftNotification = async (storyId, recipientName) => {
    const confirmResend = window.confirm(
      `Are you sure you want to resend the gift notification${recipientName ? ` to ${recipientName}` : ''}?`
    );
    
    if (!confirmResend) return;

    try {
      setSuccessMessage(`Resending gift notification...`);
      const result = await fn.resendGiftStoryNotification({ storyId });
      
      if (result.data.success) {
        setSuccessMessage(`Gift notification resent successfully!`);
        console.log('Gift notification resent:', result.data.message);
      }
    } catch (error) {
      console.error('Error resending gift notification:', error);
      alert(`Failed to resend gift notification: ${error.message}`);
      setSuccessMessage(null);
    }
  };

  const handleUpdateRecipientEmail = async (storyId, currentEmail, currentName) => {
    const newEmail = prompt(
      'Enter the new recipient email address:', 
      currentEmail || ''
    );
    
    if (!newEmail || newEmail === currentEmail) return;

    const newName = prompt(
      'Enter the recipient name (optional):', 
      currentName || ''
    );

    try {
      setSuccessMessage(`Updating recipient email...`);
      const result = await fn.updateGiftRecipientEmail({ 
        storyId, 
        newRecipientEmail: newEmail,
        newRecipientName: newName || undefined
      });
      
      if (result.data.success) {
        setSuccessMessage(`Recipient email updated successfully!`);
        console.log('Recipient email updated:', result.data);
        // The story list will refresh automatically due to the Firestore listener
      }
    } catch (error) {
      console.error('Error updating recipient email:', error);
      if (error.message.includes('already been claimed')) {
        alert('Cannot update email - this gift has already been claimed by the recipient.');
      } else if (error.message.includes('Invalid email')) {
        alert('Please enter a valid email address.');
      } else {
        alert(`Failed to update recipient email: ${error.message}`);
      }
      setSuccessMessage(null);
    }
  };

  // Helper functions for collaborative features
  // eslint-disable-next-line no-unused-vars
  const handleAddHelper = async (storyId) => {
    const helperEmail = prompt(
      'Enter the email address of the family member who will help:'
    );
    
    if (!helperEmail || !helperEmail.includes('@')) {
      if (helperEmail) alert('Please enter a valid email address.');
      return;
    }

    const helperName = prompt(
      'Enter their name (optional):',
      ''
    );

    const relationship = prompt(
      'What is their relationship to the story recipient? (optional):',
      'Family member'
    );

    const accessLevel = window.confirm(
      'Should they be able to help edit the story?\n\n' +
      'Click "OK" for Helper access (can edit and assist)\n' +
      'Click "Cancel" for Viewer access (can only read)'
    ) ? 'helper' : 'viewer';

    try {
      setSuccessMessage(`Adding helper...`);
      
      const result = await fn.addStoryHelper({ 
        storyId, 
        helperEmail,
        helperName: helperName || undefined,
        relationship: relationship || undefined,
        accessLevel
      });
      
      if (result.data.success) {
        setSuccessMessage(`Helper added successfully! They can now access the story.`);
        console.log('Helper added:', result.data);
        // The story list will refresh automatically due to the Firestore listener
      }
    } catch (error) {
      console.error('Error adding helper:', error);
      if (error.message.includes('already-exists')) {
        alert('This person is already a helper for this story.');
      } else if (error.message.includes('permission-denied')) {
        alert('You don\'t have permission to add helpers to this story.');
      } else {
        alert(`Failed to add helper: ${error.message}`);
      }
      setSuccessMessage(null);
    }
  };

  // Sharing link functions
  // Generate sharing link for gift stories
  // eslint-disable-next-line no-unused-vars
  const handleGenerateSharingLink = async (storyId) => {
    setLoadingSharingLink(true);
    try {
      const result = await fn.generateGiftStoryLink({ storyId });
      if (result.data.success) {
        setSharingModal({
          isOpen: true,
          storyId,
          links: result.data.links,
          recipientInfo: result.data.recipientInfo
        });
      }
    } catch (error) {
      console.error('Error generating sharing link:', error);
      alert('Failed to generate sharing link. Please try again.');
    } finally {
      setLoadingSharingLink(false);
    }
  };

  const handleCloseSharingModal = () => {
    setSharingModal({ isOpen: false, storyId: null, links: null });
  };

  // Secure story sharing function
  const handleShareStory = (story) => {
    setSecureShareModal({ isOpen: true, story });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Link copied to clipboard!');
    });
  };

  const renderStoryList = (storyList, isGiftStory = false) => {
    if (storyList.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isGiftStory ? 'No gift stories yet' : 'No stories yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {isGiftStory 
              ? 'Create a story as a gift for someone special'
              : 'Create your first story to start capturing memories'
            }
          </p>
          {!isGiftStory && (
            canCreateStory ? (
              <Link to="/story/new" className="memory-stream-button">
                Create Your First Story
              </Link>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">You need to purchase a Memory Stream package to create stories</p>
                <a 
                  href="https://memorystream.co" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="memory-stream-button inline-flex items-center"
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-2" />
                  Purchase Memory Stream
                </a>
              </div>
            )
          )}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {storyList.map((story) => {
          const questionCount = story.questionCount || story.questions?.length || 0;
          const answeredCount = userStats?.stories?.find(s => s.id === story.id)?.answerCount || 0;
          const completionRate = questionCount > 0 ? Math.round((answeredCount / questionCount) * 100) : 0;
          
          return (
            <div key={story.id} className="memory-stream-card hover:shadow-lg transition-shadow duration-200 flex flex-col h-full">
              {/* Full Width Cover Photo at Top */}
              {story.coverPhoto ? (
                <div className="w-full aspect-[4/3] mb-4">
                  <img
                    src={story.coverPhoto.url}
                    alt="Story cover"
                    className="w-full h-full object-cover rounded-lg border border-gray-200"
                  />
                </div>
              ) : (
                <div className="w-full aspect-[4/3] bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
                  <BookOpenIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-gray-400" />
                </div>
              )}

              {/* Story Content - Flex Grow */}
              <div className="flex-grow">
                {/* Story Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-1 line-clamp-2">
                      {story.title}
                    </h3>
                    {isGiftStory && (
                      <div className="text-sm mb-2 space-y-1">
                        <div className="flex items-center justify-center sm:justify-start text-blue-600">
                          🎁 Gift for {story.recipientName}
                        </div>
                        {story.recipientEmail && (
                          <div className="text-gray-500 text-xs text-center sm:text-left">
                            {story.recipientEmail}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Role Label for Collaborative Stories */}
                    {story.roleLabel && (
                      <div className="text-sm mb-2">
                        <div className="flex items-center justify-center sm:justify-start text-purple-600">
                          {story.roleLabel}
                        </div>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                      {story.description || story.storyTopic || 'No description'}
                    </p>
                  </div>
                </div>
                {isGiftStory && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2 flex-shrink-0">
                    Gift
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              {questionCount > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                    <span>{answeredCount} of {questionCount} questions</span>
                    <span>{completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        completionRate === 100 ? 'bg-green-500' : 
                        completionRate >= 50 ? 'bg-blue-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${completionRate}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Story Info */}
              <div className="flex items-center justify-end text-sm text-gray-500 mb-4">
                <span>
                  {story.updatedAt?.toDate?.()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) || 
                   story.createdAt?.toDate?.()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) || 'Recent'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Special treatment for pending collaborative invitations */}
                {story.collaborationStatus === 'pending' && story.userRole === 'collaborator' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start space-x-2 mb-2">
                      <span className="text-purple-600 text-lg">📬</span>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-purple-900">Collaboration Invitation</div>
                        <div className="text-xs text-purple-700 mt-0.5">
                          Click below to accept and start collaborating!
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Primary Open Button - Full Width */}
                <Link
                  to={story.isCollaborativeStory ? `/collaborative-story/${story.id}` : `/story/${story.id}`}
                  className="text-white font-medium py-2.5 rounded-md transition-colors duration-200 w-full text-center block"
                  style={{ 
                    backgroundColor: story.collaborationStatus === 'pending' && story.userRole === 'collaborator' 
                      ? '#9333ea' 
                      : 'var(--ms-secondary)' 
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = story.collaborationStatus === 'pending' && story.userRole === 'collaborator' 
                    ? '#7e22ce' 
                    : 'var(--ms-secondary-light)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = story.collaborationStatus === 'pending' && story.userRole === 'collaborator' 
                    ? '#9333ea' 
                    : 'var(--ms-secondary)'}
                >
                  {story.collaborationStatus === 'pending' && story.userRole === 'collaborator' 
                    ? '✅ Accept Invitation & Start Collaborating' 
                    : 'Open Story'}
                </Link>
                
                {/* Secondary Action Buttons - Full Width Stacked */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleExportPDF(story.id, story.title)}
                    disabled={answeredCount === 0}
                    className="memory-stream-button-secondary w-full text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={answeredCount === 0 ? "Answer some questions first" : "Export to PDF"}
                  >
                    📄 Export PDF
                  </button>

                  {/* Share Story Button - For all stories */}
                  <button
                    onClick={() => handleShareStory(story)}
                    className="memory-stream-button-secondary w-full text-sm py-2 text-green-600 hover:text-green-800"
                    title="Share securely via email invitation"
                  >
                    🔗 Share Story via Email
                  </button>

                  {/* Reminder Button + Dropdown */}
                  <div className="w-full relative" ref={reminderStoryId === story.id ? reminderDropdownRef : null}>
                    <button
                      onClick={() => openReminderPanel(story.id)}
                      className={`memory-stream-button-secondary w-full text-sm py-2 text-indigo-600 hover:text-indigo-800 ${reminderStoryId === story.id ? 'ring-2 ring-indigo-300' : ''}`}
                      title="Configure email reminders for this story"
                    >
                      ⏰ Reminders {reminderStoryId === story.id ? '▲' : '▼'}
                    </button>
                    {reminderStoryId === story.id && (
                      <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-30 p-3 animate-fadeIn">
                        {reminderLoading ? (
                          <div className="text-xs text-gray-500 py-2 text-center">Loading...</div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-[11px] leading-snug text-gray-600">Choose how often to get an email reminder to add more memories.</p>
                            <div className="grid grid-cols-2 gap-2">
                              {['off','weekly','biweekly','monthly'].map(c => (
                                <button
                                  key={c}
                                  disabled={reminderSaving}
                                  onClick={()=>saveReminderCadence(c)}
                                  className={`text-[11px] px-2 py-2 rounded border transition ${reminderSettings.cadence===c ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-indigo-50 border-gray-300'} disabled:opacity-50`}
                                >
                                  {c === 'off' ? 'Off' : c==='weekly' ? 'Weekly' : c==='biweekly' ? 'Every 2 Weeks' : 'Monthly'}
                                </button>
                              ))}
                            </div>
                            {reminderSettings.lastSentAt && (
                              <p className="text-[10px] text-gray-500">Last sent: {reminderSettings.lastSentAt.toDate?.()?.toLocaleDateString?.() || ''}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Resend Email Button - Only for Gift Stories */}
                  {isGiftStory && (
                    <button
                      onClick={() => handleResendGiftNotification(story.id, story.recipientName)}
                      className="memory-stream-button-secondary w-full text-sm py-2 text-blue-600 hover:text-blue-800"
                      title="Resend gift notification email"
                    >
                      📧 Resend Gift Email
                    </button>
                  )}

                  {/* Edit Email Button - Only for Unclaimed Gift Stories */}
                  {isGiftStory && !story.giftTransferredAt && (
                    <button
                      onClick={() => handleUpdateRecipientEmail(story.id, story.recipientEmail, story.recipientName)}
                      className="memory-stream-button-secondary w-full text-sm py-2 text-orange-600 hover:text-orange-800"
                      title="Update recipient email address"
                    >
                      ✏️ Edit Recipient Email
                    </button>
                  )}

                  {/* Developer Delete Button */}
                  {developerMode && (
                    <button
                      onClick={() => handleDeleteStory(story.id, story.title)}
                      className="memory-stream-button-secondary w-full text-sm py-2 text-red-600 hover:text-red-800"
                      title="Delete story permanently"
                    >
                      🗑️ Delete Story
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-green-400">✅</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 space-y-4 sm:space-y-0 mt-2 sm:mt-0">
        <div className="px-2 sm:px-0">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold ms-heading" style={{ color: 'var(--ms-primary)' }}>
            Welcome to Memory Stream{currentUser?.displayName ? `, ${currentUser.displayName}` : ''}!
          </h1>
          <p className="text-gray-600 mt-2">Capture and preserve life stories with personalised questions</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {userStats ? (
          // Loaded stats
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BookOpenIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Stories</p>
                  <p className="text-2xl font-semibold text-gray-900">{userStats.totalStories}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <SparklesIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Questions Answered</p>
                  <p className="text-2xl font-semibold text-gray-900">{userStats.totalAnswers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-semibold">Q</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Questions</p>
                  <p className="text-2xl font-semibold text-gray-900">{userStats.totalQuestions}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-600 font-semibold">%</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                  <p className="text-2xl font-semibold text-gray-900">{userStats.overallCompletionRate}%</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Skeleton loading
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse w-12"></div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse w-12"></div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse w-12"></div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse w-16"></div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Personal Stories Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Story Projects</h2>
        {renderStoryList(stories, false)}
      </div>

      {/* Received Gift Stories Section */}
      {receivedGiftStories.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mr-3">Gifts Received</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
              {receivedGiftStories.length}
            </span>
          </div>
          <div className="bg-purple-50 rounded-md p-4 mb-6">
            <p className="text-sm text-purple-800">
              🎁 These are special stories gifted to you. You can answer questions, add photos, and preserve your memories.
            </p>
          </div>
          {renderStoryList(receivedGiftStories, true)}
        </div>
      )}

      {/* Gift Stories Section */}
      {myGiftStories.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mr-3">Gift Stories You've Created</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {myGiftStories.length}
            </span>
          </div>
          <div className="bg-blue-50 rounded-md p-4 mb-6">
            <p className="text-sm text-blue-800">
              📝 These are stories you've created as gifts for others. You have full access to assist with the story creation process, add questions, and manage content.
            </p>
          </div>
          {renderStoryList(myGiftStories, true)}
        </div>
      )}

      {/* Collaborative Stories Section */}
      {collaborativeStories.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mr-3">Collaborative Stories</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {collaborativeStories.length}
            </span>
          </div>
          <div className="bg-green-50 rounded-md p-4 mb-6">
            <p className="text-sm text-green-800">
              👥 Stories you're collaborating on with others. These may include gifts you've received, stories you're co-authoring, or collaborative gifts you've given.
            </p>
          </div>
          {renderStoryList(collaborativeStories.map(story => ({
            ...story,
            // Add role indicator for display
            roleLabel: story.userRole === 'gift_recipient' ? '🎁 Gifted to you' :
                      story.userRole === 'gift_giver' ? '🎁 Gift you gave' :
                      story.userRole === 'collaborator' ? '👥 Collaborating' :
                      story.userRole === 'owner' ? '👤 Your story' : ''
          })), false)}
        </div>
      )}

      {/* Shared Stories Section */}
      {sharedStories.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mr-3">Shared with Me</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {sharedStories.length}
            </span>
          </div>
          <div className="bg-blue-50 rounded-md p-4 mb-6">
            <p className="text-sm text-blue-800">
              📬 Stories that have been shared with you via email invitation. You have {sharedStories.find(s => s.accessLevel === 'editor') ? 'editing' : 'viewing'} access.
            </p>
          </div>
          {renderStoryList(sharedStories.map(story => ({
            ...story,
            // Add access level indicator
            roleLabel: story.accessLevel === 'editor' ? '✏️ Editor access' : '👁️ Viewer access'
          })), false)}
        </div>
      )}

      {/* Developer Mode Toggle */}
      <div className="text-center mt-12 pt-8 border-t border-gray-200">
        <button
          onClick={() => setDeveloperMode(!developerMode)}
          className={`px-6 py-3 rounded-md font-medium transition-colors duration-200 ${
            developerMode 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-gray-600 hover:bg-gray-700 text-white'
          }`}
          title={developerMode ? 'Hide delete buttons' : 'Show delete buttons for story management'}
        >
          {developerMode ? '🔒 Hide Delete Options' : 'Delete Mode'}
        </button>
        {developerMode && (
          <p className="text-red-600 text-sm mt-2 font-medium">
            ⚠️ Delete buttons are now visible - use with caution!
          </p>
        )}
      </div>

      {/* Sharing Modal */}
      {sharingModal.isOpen && sharingModal.links && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4"
          style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    🔗 Share Your Story
                  </h3>
                  <p className="text-sm text-gray-600">
                    For: <span className="font-medium text-gray-900">{sharingModal.recipientInfo?.name || 'Gift Recipient'}</span>
                  </p>
                </div>
                <button
                  onClick={handleCloseSharingModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-8">
              {/* Introduction */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Multiple Ways to Share
                </h4>
                <p className="text-gray-600 max-w-md mx-auto">
                  Share this story with family and friends. No login required for recipients!
                </p>
              </div>

              {/* Sharing Options Grid */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Direct Link Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Direct Link</h5>
                      <p className="text-sm text-blue-700">Perfect for email & messaging</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={sharingModal.links.direct}
                        readOnly
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(sharingModal.links.direct)}
                        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Link
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                        📧 Email
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                        � Text Message
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                        📱 Social Media
                      </span>
                    </div>
                  </div>
                </div>

                {/* QR Code Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">QR Code</h5>
                      <p className="text-sm text-green-700">Great for print & mobile</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-green-300">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gray-200 rounded-lg mx-auto mb-3 flex items-center justify-center">
                          <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm8 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
                          </svg>
                        </div>
                        <p className="text-sm text-gray-600 font-medium">QR Code Preview</p>
                        <p className="text-xs text-gray-500 mt-1">Coming Soon</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => copyToClipboard(sharingModal.links.qrData)}
                      className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy QR Data
                    </button>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-200 text-green-800">
                        🖨️ Print Materials
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-200 text-green-800">
                        📱 Mobile Scan
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Tips */}
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h6 className="font-semibold text-purple-900 mb-3">💡 How to Use These Links</h6>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex items-start">
                        <span className="text-purple-600 mr-2 font-bold">1.</span>
                        <p className="text-sm text-purple-800">Share the direct link via email, text, or social media</p>
                      </div>
                      <div className="flex items-start">
                        <span className="text-purple-600 mr-2 font-bold">2.</span>
                        <p className="text-sm text-purple-800">Generate QR codes for print materials like cards or letters</p>
                      </div>
                      <div className="flex items-start">
                        <span className="text-purple-600 mr-2 font-bold">3.</span>
                        <p className="text-sm text-purple-800">Recipients can access without creating an account</p>
                      </div>
                      <div className="flex items-start">
                        <span className="text-purple-600 mr-2 font-bold">4.</span>
                        <p className="text-sm text-purple-800">Links are secure and specific to this story</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Links are ready to share!
                </div>
                <button
                  onClick={handleCloseSharingModal}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Secure Story Share Modal */}
      <SecureStoryShareModal
        isOpen={secureShareModal.isOpen}
        onClose={() => setSecureShareModal({ isOpen: false, story: null })}
        story={secureShareModal.story}
      />

      {/* PDF Generation Modal */}
      {pdfGenerationModal.isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
        >
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Export PDF</h2>
                <button
                  onClick={() => setPdfGenerationModal({ isOpen: false, storyId: null, storyTitle: null })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Story: {pdfGenerationModal.storyTitle}</h3>
              </div>
              
              <PDFGenerator
                storyId={pdfGenerationModal.storyId}
                storyTitle={pdfGenerationModal.storyTitle}
                onWizardOpen={() => {}}
                onWizardClose={() => setPdfGenerationModal({ isOpen: false, storyId: null, storyTitle: null })}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};
